import { Box, Button, Chip, Grid, Typography, useMediaQuery } from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { COURSE_LEVELS, getCourseLevelLabel } from "../../constants/courseLevels";
import { fetchApprovedCourses } from "../../redux/Actions/courseAdminApprovalActions";
import CourseCard from "../CourseCard/CourseCard";
import CourseInterestCard from "../CourseInterest/CourseInterestCard";
import api from "lib/api/api";

const CoursesTile = () => {
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const { approvedCourses } = useSelector(
    (state) => state.courseApprovals || {}
  );

  const [searchParams] = useSearchParams();
  const requestedLevel = searchParams.get("level");
  const initialLevel = COURSE_LEVELS.includes(requestedLevel) ? requestedLevel : "A1";
  const [selectedLevel, setSelectedLevel] = useState(initialLevel);
  const [interestOfferings, setInterestOfferings] = useState([]);

  useEffect(() => {
    dispatch(fetchApprovedCourses());
    api.get("/course-interest-offerings").then((response) => setInterestOfferings(response.data || [])).catch(() => setInterestOfferings([]));
  }, [dispatch]);

  const isXl = useMediaQuery((theme) => theme.breakpoints.only("xl"));

  const defaultCount = isXl ? 3 : 4;

  const filteredCourses =
    approvedCourses?.find((group) => group.parentTitle === selectedLevel)
      ?.courses || [];
  const levelInterestOfferings = interestOfferings.filter((offering) => offering.level === selectedLevel);

  const levels = COURSE_LEVELS;

  const [showAll, setShowAll] = useState(false);

  const language = i18n.language?.startsWith("ar") ? "ar" : i18n.language?.startsWith("de") ? "de" : "en";
  const courseTypeCopy = {
    en: {
      available: "Available courses: fixed dates and teacher, ready to book",
      interest: "Upcoming courses: register interest only, not bookable yet",
    },
    de: {
      available: "Buchbare Kurse: feste Termine und Lehrkraft",
      interest: "Geplante Kurse: nur Interesse anmelden, noch nicht buchbar",
    },
    ar: {
      available: "الدورات المتاحة: لها مواعيد ومدرّس ويمكن حجزها الآن",
      interest: "الدورات القادمة: لتسجيل الاهتمام فقط وغير متاحة للحجز بعد",
    },
  }[language];

  const shownCourses = showAll
    ? filteredCourses
    : filteredCourses.slice(0, defaultCount);

  return (
    <Box
      id={"courses-section"}
      sx={{
        padding: { xs: 2, sm: 3 },
        px: { xs: 2, sm: 3, md: "120px" },
        my: { xs: 4, sm: 6 },
      }}
    >
      <Typography
        variant="h6"
        fontWeight="bold"
        mb={2}
        sx={{ fontSize: { xs: "1.2rem", sm: "1.5rem", md: "1.75rem" } }}
      >
        {t("chooseLevelTitle")}
      </Typography>

      {/* Level Selection */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: { xs: 1, sm: 1.5 },
          mb: { xs: 2.5, sm: 4 },
        }}
      >
        {levels.map((level) => (
          <Button
            key={level}
            onClick={() => {
              setSelectedLevel(level);
              setShowAll(false);
            }}
            sx={{
              minWidth: { xs: "48px", sm: "56px", md: "60px" },
              borderRadius: "30px",
              border: "1px solid #ccc",
              backgroundColor:
                selectedLevel === level ? "#FFC107" : "transparent",
              color: selectedLevel === level ? "#000" : "#333",
              fontWeight: selectedLevel === level ? "bold" : "normal",
              px: { xs: "12px", sm: "20px", md: "40px" },
              py: { xs: 0.5, sm: 0.6, md: 0.8 },
              fontSize: { xs: "0.8rem", sm: "0.9rem" },
              "&:hover": {
                backgroundColor:
                  selectedLevel === level ? "#FFB300" : "#f9f9f9",
              },
            }}
          >
            {getCourseLevelLabel(t, level)}
          </Button>
        ))}
      </Box>

      {shownCourses.length === 0 && levelInterestOfferings.length === 0 ? (
        <Typography color="text.secondary" mt={3}>
          {t("noCoursesAvailable")}
        </Typography>
      ) : (
        <>
          <Grid container spacing={{ xs: 2, md: 3 }} sx={{ direction: language === "ar" ? "rtl" : "ltr", alignItems: "flex-start" }}>
            {levelInterestOfferings.length > 0 && (
              <Grid item xs={12} md={shownCourses.length > 0 ? 4 : 12}>
                <Box sx={{ direction: language === "ar" ? "rtl" : "ltr" }}>
                  <Chip label={courseTypeCopy.interest} sx={{ maxWidth: "100%", height: "auto", mb: 2, bgcolor: "#D7DADD", color: "#343A40", fontWeight: 800, "& .MuiChip-label": { display: "block", whiteSpace: "normal", py: .65 } }} />
                  <Grid container spacing={{ xs: 2, md: 3 }}>
                    {levelInterestOfferings.map((offering) => (
                      <Grid key={`interest-${offering.id}`} item xs={12}>
                        <CourseInterestCard offering={offering} />
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              </Grid>
            )}

            {shownCourses.length > 0 && (
              <Grid item xs={12} md={levelInterestOfferings.length > 0 ? 8 : 12}>
                <Box sx={{ direction: language === "ar" ? "rtl" : "ltr" }}>
                  <Chip label={courseTypeCopy.available} sx={{ maxWidth: "100%", height: "auto", mb: 2, bgcolor: "#FFD600", color: "#111", fontWeight: 800, "& .MuiChip-label": { display: "block", whiteSpace: "normal", py: .65 } }} />
                  <Grid container spacing={{ xs: 2, md: 3 }}>
                    {shownCourses.map((course) => (
                      <Grid key={course.teacherCourseId} item xs={12} sm={6}>
                        <Box sx={{ width: "100%", minWidth: 0 }}>
                          <CourseCard {...course} />
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              </Grid>
            )}
          </Grid>
          {filteredCourses.length > defaultCount && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 5 }}>
              <Button
                variant="contained"
                onClick={() => setShowAll((prev) => !prev)}
                sx={{
                  borderRadius: "50%",
                  minWidth: 0,
                  width: 32,
                  height: 32,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
                title={showAll ? t("showLess") : t("showMore")}
              >
                <span
                  style={{
                    fontSize: 26,
                    transform: showAll ? "rotate(180deg)" : "none",
                  }}
                >
                  &#8595;
                </span>
              </Button>
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default CoursesTile;
